import { setPlatformAdminActive } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { PlatformSuperAdminListItem } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface ToggleAdminActiveDialogProps {
  member: PlatformSuperAdminListItem | null;
  onClose: () => void;
  onToggled: (member: PlatformSuperAdminListItem, active: boolean, sessionsRevoked: number) => void;
}

// super-admin-deactivate-users — one dialog handles both directions (like
// TogglePlanActiveDialog), but owns the API call itself (like DeactivateDialog)
// since the deactivate path needs to surface the revoked-session count back
// to the page's success toast.
export function ToggleAdminActiveDialog({
  member,
  onClose,
  onToggled,
}: ToggleAdminActiveDialogProps) {
  const { t } = useTranslation('users');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deactivating = member?.active ?? true;

  useEffect(() => {
    if (!member) {
      setSubmitting(false);
      setError(null);
    }
  }, [member]);

  async function confirm() {
    if (!member) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await setPlatformAdminActive(member.id, !member.active);
      onToggled(member, result.active, result.sessionsRevoked);
      onClose();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('toggleAdminActiveDialog.genericError') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!member} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {deactivating
              ? t('toggleAdminActiveDialog.deactivateTitle')
              : t('toggleAdminActiveDialog.reactivateTitle')}
          </DialogTitle>
          <DialogDescription>
            {deactivating
              ? t('toggleAdminActiveDialog.deactivateDescription')
              : t('toggleAdminActiveDialog.reactivateDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('toggleAdminActiveDialog.adminLabel')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{member?.email}</p>
        </div>

        {error && (
          <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant={deactivating ? 'destructive' : 'default'}
            size="sm"
            onClick={() => void confirm()}
            disabled={submitting}
          >
            {submitting
              ? t('toggleAdminActiveDialog.saving')
              : deactivating
                ? t('toggleAdminActiveDialog.deactivate')
                : t('toggleAdminActiveDialog.reactivate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
